/**
 * Quantity Meter Bundle - Custom Element
 * Version: 1.0.0
 *
 * Écoute l'événement 'bundleBuilderQuantityChanged' dispatché par bundle-builder
 * et met à jour la progression visuelle en temps réel
 *
 * Features:
 * - Progression visuelle (ligne + cercles)
 * - Badge livraison offerte (configurable)
 * - Support remise globale (visuelle uniquement)
 * - 4 ou 5 paliers configurables
 * - Animation fluide
 * - Responsive
 */

class QuantityMeterBundleElement extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.showShippingBadge = this.dataset.showShippingBadge === 'true';
    this.shippingThreshold = parseInt(this.dataset.shippingThreshold) || 2;
    this.shippingBadgeText = this.dataset.shippingBadgeText || 'Livraison offerte';
    this.enableGlobalDiscount = this.dataset.enableGlobalDiscount === 'true';
    this.globalDiscountPercentage = parseFloat(this.dataset.globalDiscountPercentage) || 0;
    this.globalDiscountColor = this.dataset.globalDiscountColor || '#dc2626';

    // Paliers (définis via data-tiers)
    this.tiers = [];
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (e) {
      console.error('[QuantityMeterBundle] Erreur parsing tiers:', e);
      this.tiers = [
        { qty: 1, text: '1 livre', price: '29€/unité' },
        { qty: 2, text: '2 livres', price: '28€/unité' },
        { qty: 3, text: '3 livres', price: '27€/unité' },
        { qty: 4, text: '4+ livres', price: '26€/unité' }
      ];
    }

    // Éléments DOM
    this.meterElement = null;
    this.shippingBadge = null;
    this.progressLine = null;
    this.items = [];

    // État actuel
    this.currentQuantity = 0;
  }

  connectedCallback() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Récupérer les éléments DOM
    this.meterElement = this.querySelector('[data-quantity-meter-bundle]');
    this.shippingBadge = this.querySelector('[data-shipping-badge]');
    this.progressLine = this.querySelector('[data-progress-line]');
    this.items = Array.from(this.querySelectorAll('.quantity-meter-bundle__item'));

    if (!this.meterElement) {
      console.error('[QuantityMeterBundle] Élément meter non trouvé');
      return;
    }

    // Appliquer la remise globale si activée
    if (this.enableGlobalDiscount && this.globalDiscountPercentage > 0) {
      this.applyGlobalDiscount();
      this.meterElement.classList.add('quantity-meter-bundle--global-discount');
    }

    // Écouter l'événement du bundle builder
    this.listenToBundleBuilder();

    // Initialiser l'état visuel
    this.updateVisualState(0);
  }

  /**
   * Écouter l'événement 'bundleBuilderQuantityChanged'
   */
  listenToBundleBuilder() {
    document.addEventListener('bundleBuilderQuantityChanged', (e) => {
      const { totalQuantity, totalPrice, selections, completedSteps } = e.detail;

      // Mettre à jour l'état
      this.currentQuantity = totalQuantity || 0;

      // Mettre à jour l'affichage
      this.updateVisualState(this.currentQuantity);

      // Dispatcher l'information de remise du palier actuel
      this.dispatchTierDiscountEvent();
    });
  }

  /**
   * Mettre à jour l'état visuel du quantity meter
   * @param {number} totalQuantity - Quantité totale sélectionnée
   */
  updateVisualState(totalQuantity) {
    // Mettre à jour le badge livraison
    this.updateShippingBadge(totalQuantity);

    // Mettre à jour la progression (cercles + ligne)
    this.updateProgress(totalQuantity);
  }

  /**
   * Mettre à jour le badge livraison offerte
   */
  updateShippingBadge(totalQuantity) {
    // Ne pas traiter si le badge n'est pas activé ou n'existe pas
    if (!this.showShippingBadge || !this.shippingBadge) return;

    const isUnlocked = totalQuantity >= this.shippingThreshold;

    // Le badge n'apparaît que si le seuil est atteint (jamais grisé)
    if (isUnlocked) {
      this.shippingBadge.classList.add('visible');
      this.shippingBadge.classList.add('active');
    } else {
      this.shippingBadge.classList.remove('visible');
      this.shippingBadge.classList.remove('active');
    }
  }

  /**
   * Mettre à jour la progression (cercles + ligne)
   */
  updateProgress(totalQuantity) {
    let currentTier = 0;
    let lastActiveTier = -1;

    // 1. Mettre à jour les cercles et déterminer le dernier palier actif
    this.tiers.forEach((tier, index) => {
      const item = this.items[index];
      if (!item) return;

      const circle = item.querySelector('.quantity-meter-bundle__item-circle');
      const circleInner = item.querySelector('.quantity-meter-bundle__item-circle-inner');
      const isReached = totalQuantity >= tier.qty;

      if (isReached) {
        if (circle) circle.classList.add('filled');
        if (circleInner) circleInner.classList.add('filled');
        lastActiveTier = index;
        currentTier = index;
      } else {
        if (circle) circle.classList.remove('filled');
        if (circleInner) circleInner.classList.remove('filled');
      }
    });

    // 2. Mettre à jour les prix et textes (seul le dernier palier actif a un prix actif)
    this.tiers.forEach((tier, index) => {
      const item = this.items[index];
      if (!item) return;

      const priceElement = item.querySelector('.quantity-meter-bundle__item-price');
      const textElement = item.querySelector('.quantity-meter-bundle__item-text');
      const isPriceActive = (index === lastActiveTier);

      if (priceElement) {
        if (isPriceActive) {
          priceElement.classList.remove('subdued');
        } else {
          priceElement.classList.add('subdued');
        }
      }

      // Activer/désactiver le texte
      if (textElement) {
        if (isPriceActive) {
          textElement.classList.add('active');
        } else {
          textElement.classList.remove('active');
        }
      }
    });

    // 3. Mettre à jour la ligne de progression
    this.updateProgressLine(currentTier);
  }

  /**
   * Mettre à jour la ligne de progression animée
   */
  updateProgressLine(currentTier) {
    if (!this.progressLine || this.items.length === 0) return;

    const firstCircle = this.items[0]?.querySelector('.quantity-meter-bundle__item-circle');
    const lastCircle = this.items[this.items.length - 1]?.querySelector('.quantity-meter-bundle__item-circle');
    const activeCircle = this.items[currentTier]?.querySelector('.quantity-meter-bundle__item-circle');

    if (!firstCircle || !lastCircle || !activeCircle) return;

    const CORRECTION = 10;

    const firstCenter = firstCircle.offsetLeft + firstCircle.offsetWidth / 2 + CORRECTION;
    const lastCenter = lastCircle.offsetLeft + lastCircle.offsetWidth / 2 + CORRECTION;
    const activeCenter = activeCircle.offsetLeft + activeCircle.offsetWidth / 2 + CORRECTION;

    // Barre de fond
    const bgLine = this.querySelector('.quantity-meter-bundle__line:not(.quantity-meter-bundle__line--filled)');
    if (bgLine) {
      bgLine.style.left = firstCenter + 'px';
      bgLine.style.width = (lastCenter - firstCenter) + 'px';
    }

    // Barre active (avec animation)
    if (this.progressLine) {
      if (currentTier > 0) {
        this.progressLine.style.left = firstCenter + 'px';
        this.progressLine.style.width = (activeCenter - firstCenter) + 'px';
      } else {
        this.progressLine.style.left = firstCenter + 'px';
        this.progressLine.style.width = '0';
      }
    }
  }

  /**
   * Appliquer la remise globale visuelle sur les prix
   */
  applyGlobalDiscount() {
    const priceElements = this.querySelectorAll('.quantity-meter-bundle__item-price[data-original-price]');

    priceElements.forEach((priceEl) => {
      const originalPriceText = priceEl.dataset.originalPrice;

      // Regex pour extraire le prix (formats: "25€", "25€/unité", "25,00€", "$25")
      const priceRegex = /(\d+(?:[.,]\d+)?)\s*([€$£])/;
      const match = originalPriceText.match(priceRegex);

      if (match) {
        const originalPrice = parseFloat(match[1].replace(',', '.'));
        const currency = match[2];
        const suffix = originalPriceText.substring(match.index + match[0].length);

        // Calculer le prix avec remise
        const discountedPrice = originalPrice * (1 - this.globalDiscountPercentage / 100);

        // Formater le prix
        let formattedDiscountedPrice;
        if (originalPriceText.includes(',') || originalPriceText.includes('.')) {
          formattedDiscountedPrice = discountedPrice.toFixed(2).replace('.', ',');
        } else {
          formattedDiscountedPrice = Math.round(discountedPrice).toString();
        }

        // Construire le HTML avec prix barré + prix soldé
        priceEl.innerHTML = `
          <span class="quantity-meter-bundle__item-price--original">${originalPriceText}</span>
          <span class="quantity-meter-bundle__item-price--discounted" style="color: ${this.globalDiscountColor};">
            ${formattedDiscountedPrice}${currency}${suffix}
          </span>
        `;
      }
    });
  }

  /**
   * Dispatcher l'événement de remise du palier actuel
   */
  dispatchTierDiscountEvent() {
    // Trouver le palier actuel (dernier palier atteint)
    let currentTier = null;
    let currentTierIndex = -1;

    this.tiers.forEach((tier, index) => {
      if (this.currentQuantity >= tier.qty) {
        currentTier = tier;
        currentTierIndex = index;
      }
    });

    // Dispatcher l'événement avec les informations du palier actuel
    document.dispatchEvent(new CustomEvent('quantityMeterTierChanged', {
      detail: {
        tierIndex: currentTierIndex,
        tier: currentTier,
        discount: currentTier ? currentTier.discount : 0,
        quantity: this.currentQuantity
      }
    }));
  }

  /**
   * Recalculer les positions de la ligne après resize
   */
  handleResize() {
    if (this.currentQuantity > 0) {
      // Trouver le palier actuel
      let currentTier = 0;
      this.tiers.forEach((tier, index) => {
        if (this.currentQuantity >= tier.qty) {
          currentTier = index;
        }
      });

      this.updateProgressLine(currentTier);
    }
  }

  disconnectedCallback() {
    // Cleanup si nécessaire
    window.removeEventListener('resize', this.handleResize.bind(this));
  }
}

// Enregistrer le custom element
customElements.define('quantity-meter-bundle', QuantityMeterBundleElement);

// Recalculer les positions au resize
window.addEventListener('resize', () => {
  const meters = document.querySelectorAll('quantity-meter-bundle');
  meters.forEach(meter => {
    if (meter.handleResize) {
      meter.handleResize();
    }
  });
});
